import { useState, useEffect, useRef, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'modules/state-mgmt/store';
import { fetchWorkerProjects, resetWorkerProjects } from 'modules/state-mgmt/workerData';
import { SortType, TabIndex } from '../types';

const SORT_CRITERIA_MAP: Record<SortType, string> = {
  [SortType.NAME_ASC]: 'projectName asc',
  [SortType.NAME_DESC]: 'projectName desc',
  [SortType.DATE_ASC]: 'lastUpdatedAt asc',
  [SortType.DATE_DESC]: 'lastUpdatedAt desc',
};

export const useProjectList = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(TabIndex.ALL);
  const [sortType, setSortType] = useState(SortType.NAME_ASC);
  const [page, setPage] = useState(1);
  const itemsPerPage = activeTab == TabIndex.ALL ? 3 : 9;
  const dispatch = useAppDispatch();
  const abortControllerRef = useRef<AbortController | null>(null);

  const { workerId, workerProjects, isWorker } = useAppSelector(state => ({
    workerId: state.onboarding?.id || state.user?.workerId,
    workerProjects: state.workerData?.workerProjects,
    isWorker: state.user.isWorker || false,
  }));

  const getStatusFromTab = (tabIndex: number): number | undefined => {
    switch (tabIndex) {
      case TabIndex.PENDING:
        return 0;
      case TabIndex.CURRENT:
        return 1;
      case TabIndex.PAST:
        return 3;
      default:
        return undefined;
    }
  };

  const loadProjects = async (tabIndex: number, pageNumber: number = 1) => {
    if (!workerId) return;

    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const status = getStatusFromTab(tabIndex);
      await dispatch(
        fetchWorkerProjects({
          workerId,
          status,
          pageNumber,
          pageSize: itemsPerPage,
          sort: sortType ? SORT_CRITERIA_MAP[sortType] : undefined,
        }),
      ).unwrap();
    } catch (err: any) {
      // Don't set error if request was aborted
      if (err?.name !== 'AbortError') {
        setError(err?.message || 'Failed to fetch projects');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Clear state before loading new projects
    dispatch(resetWorkerProjects());
  }, []);

  // Load projects when workerId, activeTab, or sortType changes
  useEffect(() => {
    if (workerId) {
      loadProjects(activeTab, 1);
      setPage(1);
    }

    // Cleanup function to abort ongoing requests on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [workerId, activeTab, sortType]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
  };

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
    loadProjects(activeTab, pageNumber);
  };

  const groupedProjects = {
    pendingProjects: workerProjects?.pending || [],
    pastProjects: workerProjects?.past || [],
    currentProjects: workerProjects?.current || [],
  };

  const allProjects = [
    ...groupedProjects.pendingProjects,
    ...groupedProjects.currentProjects,
    ...groupedProjects.pastProjects,
  ];

  const projectCounts = useMemo(() => {
    const pendingCount = workerProjects?.pagination?.pending?.totalResults ?? groupedProjects.pendingProjects.length;
    const currentCount = workerProjects?.pagination?.current?.totalResults ?? groupedProjects.currentProjects.length;
    const pastCount = workerProjects?.pagination?.past?.totalResults ?? groupedProjects.pastProjects.length;

    return {
      all: pendingCount + currentCount + pastCount,
      pending: pendingCount,
      current: currentCount,
      past: pastCount,
    };
  }, [workerProjects, groupedProjects]);

  const getPaginationInfo = () => {
    if (activeTab === TabIndex.ALL) {
      // For "All" tab, use the maximum pages needed across all categories
      const pendingPages = Math.ceil((workerProjects?.pagination?.pending?.totalResults || 0) / itemsPerPage);
      const currentPages = Math.ceil((workerProjects?.pagination?.current?.totalResults || 0) / itemsPerPage);
      const pastPages = Math.ceil((workerProjects?.pagination?.past?.totalResults || 0) / itemsPerPage);

      const maxPages = Math.max(pendingPages, currentPages, pastPages);
      const totalResults = maxPages * itemsPerPage;

      return {
        pageNumber: page,
        pageSize: itemsPerPage,
        totalResults,
      };
    }

    const statusKey = activeTab === TabIndex.PENDING ? 'pending' : activeTab === TabIndex.CURRENT ? 'current' : 'past';
    return workerProjects?.pagination?.[statusKey];
  };

  const handleSortChange = (newSortType: SortType) => {
    setError(null); // Clear errors when changing sort
    setPage(1);
    setSortType(newSortType);
  };

  const refreshProjects = async () => {
    if (workerId) {
      await loadProjects(activeTab, page);
    }
  };

  return {
    projectList: allProjects,
    projectCounts,
    groupedProjects,
    isLoading,
    error,
    activeTab,
    page,
    handleTabChange,
    handlePageChange,
    paginationInfo: getPaginationInfo(),
    handleSortChange,
    sortType,
    refreshProjects,
    isWorker,
  };
};
